import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { isAdmin, isOrgaOrAdmin } from '../../util/auth';
import { useOffline } from '../context/OfflineContext';
import { setPresence as offlineSetPresence, getPendingPresence, patchCachedParticipant } from '../../offline/offlineApi';
import {
  applyPresenceWithAutoPaid, formatEuro, owesPayment, paymentState, pickPresencePayment, summarizeOpen,
} from '../../util/payment';
import { eventRules } from '../../util/eventRules';
import { mailSlotLabel } from '../../util/mailSlots';
import {
  addOnColorStyle, addOnLineItems, available, formatAddOnPrice, formatAddOnQty, formatCapacity, hasAddOnColor,
  isExhausted, linesTotal, onSiteLines, priceFor, signupLines, signupUnitPrice, taken, takenByAddOn,
} from '../../util/addOns';
import { groupRoleLabel, resolveGroupAndRole } from '../../util/participantGroups';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import useRoleSettings from '../../hooks/useRoleSettings';
import { subscribeSync } from '../../offline/syncEngine';
import { ROUTES } from '../../util/routes';
import '../styles/MainContainer.css';
import { useContextMenu } from '../context/ContextMenuContext';
import ContextMenu from '../context-menu/ContextMenu';
import ContextMenuItem from '../context-menu/ContextMenuItem';
import StatusDot from '../status/StatusDot';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import './EventList.css';
import './EventPlayerCard.css';
import '../popup/EditPopup.css';
import '../form/Form.css';
import ListHeader from '../list/ListHeader';
import ListItem from '../list/ListItem';
import Checkbox from '../input/Checkbox';
import api from '../wrapper/API';
import { useSuccessBar } from '../context/SuccessBarContext';
import Popup from '../popup/Popup';
import { usePopup } from '../context/PopupContext';
import { useNavigate } from 'react-router-dom';
import ListTextItem from '../list/ListTextItem';
import ListSymbols from '../list/ListSymbols';
import ListSymbol from '../list/ListSymbol';
import PopupDescriptionItem from '../popup/PopupDescriptionItem';
import PopupDescriptionLinkItem from '../popup/PopupDescriptionLinkItem';
import ContextMenuSeparator from '../context-menu/ContextMenuSeparator';
import CustomComboBox from '../input/CustomComboBox';
import CustomTogglebox from '../input/CustomTogglebox';
import ListColorExtension from '../list/ListColorExtension';
import Textfield from '../input/Textfield';
import Icon from '../home/Icon';
import Tooltip from '../tooltip/Tooltip';
import '../pages/Event.css';
import '../pages/Registrations.css';
import WaitlistStatusIcon from './WaitlistStatusIcon';
import EventOfflineStatus from './EventOfflineStatus';
import PrivateLinkChip from './PrivateLinkChip';
import EventAddOnInputs from './EventAddOnInputs';
import AddOnChips from '../add-on/AddOnChips';
import ActionButton from '../input/ActionButton';
import { eventOnSiteAddOns, eventSignupAddOns, signupQuantities, withReductions } from './addOnMessages';

// Mappt PresenceStatus-Enum (Backend) auf Checkbox-State (FE: 0=undef, 1=anwesend, 2=abwesend)
function presenceToCheckboxState(value) {
  if (value === 'Present' || value === 1) return 1;
  if (value === 'Absent' || value === 0) return 2;
  return 0; // 'Other' / undefined
}

// Klick-Zyklus auf der Checkbox: Other → Present → Absent → Present → ...
function nextPresence(current) {
  switch (current) {
    case 'Present': return 'Absent';
    case 'Absent': return 'Present';
    case 'Other':
    default: return 'Present';
  }
}

// Reservation wurde abgeschafft - es gibt nur noch Confirmed/Waitlist/PendingApproval.
function isConfirmed(post) { return post.participantType === 'Confirmed' || post.participantType === 1; }
function isWaitlist(post) { return post.participantType === 'Waitlist' || post.participantType === 3; }
function isPresent(post) { return post?.presenceStatus === 'Present' || post?.presenceStatus === 1; }

const sameAddOnId = (a, b) => a != null && b != null && Number(a) === Number(b);

// Price of a participant will not be recomputed on add-on changes: paid manually (not by presence) or set by hand.
const keepsPrice = (post) => !!post?.priceOverridden || (!!post?.isPaid && !post?.paidByPresence);

// "Platz frei" mail: recognized by its mail slot; entries from before the slot system only carry the
// default textblock name.
const SPOT_AVAILABLE_SLOT = 'spot_available';
const SPOT_AVAILABLE_TEXTBLOCK = 'registration_spot_available';
function isSpotAvailableMail(mail) {
  return mail.slot === SPOT_AVAILABLE_SLOT || mail.textblockName === SPOT_AVAILABLE_TEXTBLOCK;
}
function getSpotMailSentAt(post) {
  const mail = post.sentEmails?.find(isSpotAvailableMail);
  return mail ? new Date(mail.sentAt) : null;
}

function spotMailTooltip(sentAt) {
  const label = `„${mailSlotLabel(SPOT_AVAILABLE_SLOT)}“`;
  return sentAt
    ? `Mail ${label} gesendet am ${formatDateTimeDe(sentAt)}`
    : `Warteliste – Mail ${label} noch nicht gesendet`;
}

// "Mail ausstehend": the mail rule of the participant's group expects a status mail, but none was sent yet.
// expectsAutoMail only reflects the rule — without an address (or with a deleted player) nothing is ever sent.
const hasPendingMail = (post) => !!post.expectsAutoMail && !!post.email?.trim() && (post.sentEmails?.length ?? 0) === 0;
const PENDING_MAIL_TOOLTIP = 'E-Mail ausstehend – die Statusmail wurde noch nicht versendet';


// Gesamtbetrag für einen Spieler: Preis (Eintritt + Anmelde-Zusätze) + im Event gebuchte Artikel (OnSite-Zeilen,
// nicht Teil des Preises).
function getPlayerTotal(post) {
  return (post.price ?? 0) + linesTotal(onSiteLines(post));
}

// Booked sign-up add-ons as name chips — child 4 of the card, always rendered (empty without lines) so the
// nth-child positions of EventPlayerCard.css stay stable.
function SignupAddOnChips({ post }) {
  return <AddOnChips items={addOnLineItems(signupLines(post))} compact ariaLabel={'Gebuchte Zusätze'} />;
}

// Zeilen-Klasse je Bezahlt-Zustand — färbt die Preis-Zelle (EventPlayerCard.css).
// Bezahlt-Status als kleines Icon vor dem Preis (der Preis selbst bleibt in der Akzentfarbe).
// 'none' (nichts zu zahlen / No-Show) bekommt kein Icon.
const PAY_STATE_ICON = {
  paid: { icon: 'check', className: 'paid', label: 'Bezahlt' },
  open: { icon: 'close', className: 'open', label: 'Zahlung offen' },
};

function PayStateIcon({ state }) {
  const cfg = PAY_STATE_ICON[state];
  if (!cfg) return null;
  return (
    <span className={`material-symbols-rounded payStateIcon ${cfg.className}`} role="img" aria-label={cfg.label} title={cfg.label}>
      {cfg.icon}
    </span>
  );
}

// Manually set price (PriceOverridden): survives recalculations until an admin restores the standard price.
const PRICE_OVERRIDDEN_LABEL = 'Manueller Preis';

function PriceOverrideIcon() {
  return (
    <span className="material-symbols-rounded priceOverrideIcon" role="img" aria-label={PRICE_OVERRIDDEN_LABEL} title={PRICE_OVERRIDDEN_LABEL}>
      edit
    </span>
  );
}

// Price cell of a confirmed row: pay state + total; a manual price is always shown (also 0 €) with its marker.
function PriceAmount({ post }) {
  const total = getPlayerTotal(post);
  if (total === 0 && !post.priceOverridden) return null;
  return (
    <span className="eventCardPrice">
      <PayStateIcon state={paymentState(post)} />
      {formatEuro(total)}
      {post.priceOverridden && <PriceOverrideIcon />}
    </span>
  );
}

// Group (+ role) at the event date as text next to the price — the dot's tooltip is not reachable on
// touch devices. Resolved from the snapshot fields (participantGroupId, roleSnapshot).
// Guests without a role get the dot only; the info popup always names the group.
function GroupRoleText({ post, groupsById }) {
  // Configured role names (/groups › Rollen) — re-render once they are loaded.
  useRoleSettings();
  const { group, role } = resolveGroupAndRole(post, groupsById);
  if (group?.isGuest && !role) return null;
  const label = groupRoleLabel(group, role);
  return label ? <span className="eventCardGroupLabel">{label}</span> : null;
}

function formatDateTimeDe(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

// event: EventDetailResponse (or null while loading) — its rules (link sign-up, prepayment) come from the
// process template, see util/eventRules; its addOns are the sign-up add-ons of the process (counters, edit popup)
// and the add-ons bookable on site ("Artikel"). Counters are computed live from the loaded participants.
// The card layout in EventPlayerCard.css is positional (nth-child): child 4 is always the sign-up chips container.
function EventList({posts, setPosts, eventId, onListItemClick, eventDate, addPlayer = () => {}, maxGroup = 0, setMaxGroup = () => {}, resetCreateForm = () => {}, maxPlayers = 0, pendingCount = null, offlineStatus = {}, onUnregister = () => {}, event = null, autoPaidOnPresence = false}) {
  const {startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar} = useSuccessBar();
  const {openPopup, popupData, closePopup, isPopupOpen} = usePopup();
  const { byId: groupsById } = useParticipantGroups();
  // Configured role names of the info popup label (groupRoleLabel) — re-render once they are loaded.
  useRoleSettings();
  const rules = eventRules(event);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [selectedSendUnregisterMailStatus, setSelectedSendUnregisterMailStatus] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  // Wartelisten-Bereich ist standardmäßig ausgeklappt — wird häufig benötigt.
  const [waitlistExpanded, setWaitlistExpanded] = useState(true);

  const { isOffline } = useOffline();
  // EventPlayer-IDs mit ausstehendem (gelb) bzw. fehlgeschlagenem (rot) Sync — für die per-Zeile-Badges.
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [failedIds, setFailedIds] = useState(() => new Set());
  const tapSeq = useRef(new Map());
  const refreshPending = useCallback(() => {
    getPendingPresence(eventId).then(({ pending, failed }) => {
      setPendingIds(pending);
      setFailedIds(failed);
    });
  }, [eventId]);
  useEffect(() => { refreshPending(); }, [refreshPending]);
  // Nach erfolgreichem Hintergrund-Sync die per-Zeile-Badges aktualisieren.
  useEffect(() => subscribeSync(() => refreshPending()), [refreshPending]);

  // Nur für die Anzeige gefiltert — alle Mutations-Operationen arbeiten auf dem vollen `posts`-Prop.
  const displayPosts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter(p =>
      (p.lastName ?? '').toLowerCase().includes(q) ||
      (p.firstName ?? '').toLowerCase().includes(q) ||
      `${p.lastName ?? ''} ${p.firstName ?? ''}`.toLowerCase().includes(q) ||
      `${p.firstName ?? ''} ${p.lastName ?? ''}`.toLowerCase().includes(q)
    );
  }, [posts, searchQuery]);

  // Confirmed/Orga im Hauptbereich, Waitlist in der eigenen Sektion unter der Liste.
  const confirmedDisplayPosts = useMemo(
    () => displayPosts.filter(p => isConfirmed(p)),
    [displayPosts]
  );
  const waitlistDisplayPosts = useMemo(
    () => displayPosts.filter(p => isWaitlist(p)),
    [displayPosts]
  );
  const hasWaitlistPlayers = useMemo(
    () => posts.some(p => isWaitlist(p)),
    [posts]
  );

  const confirmedCount = useMemo(
    () => posts.filter(p => p.participantType === 'Confirmed' || p.participantType === 1).length,
    [posts]
  );
  const waitlistCount = useMemo(
    () => posts.filter(p => p.participantType === 'Waitlist' || p.participantType === 3).length,
    [posts]
  );
  // Anmelde-Zusätze des Prozesses (Zähler, Bearbeiten) und im Event buchbare Zusätze (Artikel).
  const signupAddOns = useMemo(() => eventSignupAddOns(event), [event]);
  const onSiteAddOns = useMemo(() => eventOnSiteAddOns(event), [event]);
  // Belegte Menge je Zusatz, live aus der Teilnehmerliste (beide Kanäle, ohne Warteliste) — Map addOnId → Menge.
  const takenById = useMemo(() => takenByAddOn(posts), [posts]);
  // "offen"-Chip: zahlungspflichtig, unbezahlt, nicht abwesend (No-Shows zählen nicht), ohne Warteliste.
  const openPayments = useMemo(() => summarizeOpen(posts), [posts]);

  //Checkbox Anwesend
  const handleListItemClick = (event, playerId) => {
    if (new Date().setHours(0, 0, 0, 0) !== new Date(eventDate).setHours(0, 0, 0, 0)) {
      openPopup("warningchangeeventplayer", {playerId: playerId, event: event});
    } else {
      changePlayerPresence(event, playerId);
    }
  };

  const handleChangePlayerPresence = () => {
    changePlayerPresence(popupData.identifier.event, popupData.identifier.playerId);
  }

  function changePlayerPresence(event, playerId) {
    const foundPost = posts.find(post => post.id === playerId);
    if (!foundPost) return;
    const next = nextPresence(foundPost.presenceStatus);
    updatePlayerPresence(event, playerId, next);
  }

  function resetPlayerPresence(event) {
    updatePlayerPresence(event, contextMenuData.identifier, 'Other');
  }

  function updatePlayerPresence(event, playerId, newState) {
    const foundPost = posts.find(post => post.id === playerId);
    if (!foundPost) return;
    if (onListItemClick) {
      onListItemClick(event, playerId, foundPost.lastName, foundPost.firstName);
    }
    if (event.defaultPrevented) return;

    // Snapshot für den Rollback: Anwesenheit + Bezahlt-Felder (die Auto-bezahlt-Regel ändert beide).
    const previous = pickPresencePayment(foundPost);
    // Optimistisch sofort setzen (vor dem Netz) — offline wie online identisch, inkl. Auto-bezahlt-Spiegel.
    setPosts(prev => prev.map(p => (p.id === foundPost.id ? applyPresenceWithAutoPaid(p, newState, autoPaidOnPresence) : p)));
    // Nur die Antwort des LETZTEN Taps je Spieler darf die Anzeige ändern (schnelle Folge-Taps können
    // sich überholen).
    const seq = (tapSeq.current.get(foundPost.id) ?? 0) + 1;
    tapSeq.current.set(foundPost.id, seq);
    const isLatestTap = () => tapSeq.current.get(foundPost.id) === seq;

    offlineSetPresence(playerId, eventId, newState, { autoPaidOnPresence })
      .then((res) => {
        // Online bestätigt: Server-Stand übernehmen (Anwesenheit + Bezahlt, kann abweichen — z. B. bei
        // per LWW ignoriertem Tap). Offline: in Queue, Badge zeigen.
        if (isLatestTap() && !res.queued && res.data) {
          setPosts(prev => prev.map(p => (p.id === foundPost.id ? { ...p, ...pickPresencePayment(res.data) } : p)));
        }
        refreshPending();
      })
      .catch(error => {
        refreshPending();
        if (!isLatestTap()) return;
        // Echter HTTP-Fehler (kein Netz-Problem): optimistische Änderung zurückrollen.
        setPosts(prev => prev.map(p => (p.id === foundPost.id ? { ...p, ...previous } : p)));
        setError(error?.response?.data ?? "Unbekannter Fehler");
        openPopup("eventplayererror", playerId);
      })
  };

  //Confirm Unregister Popup
  const handleConfirmUnregister = () => {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (!foundPost) return;
    startSuccessBarLoading("Spieler wird abgemeldet");

    const sendMail = selectedSendUnregisterMailStatus === 0 && isConfirmed(foundPost);
    unregisterPlayerFromEvent(foundPost.id, sendMail);
    resetCreateForm();
  };

  function unregisterPlayerFromEvent(participantId, sendCancellationMail = false) {
    api.delete(`/EventPlayer/${participantId}?sendCancellationMail=${sendCancellationMail}`)
      .then(() => {
        startSuccessBarSuccess("Spieler abgemeldet")
        const updatedPosts = posts.filter(post => post.id !== participantId);
        setMaxGroup(Math.max(0, ...updatedPosts.map(post => post.group ?? 0)));
        setPosts(updatedPosts);
        // Parent benachrichtigen, damit die "Abgemeldete Spieler"-Sektion sich neu lädt.
        onUnregister(participantId);
      })
      .catch(error => {
        closeSuccessBar();
        setError(error?.response?.data ?? "Unbekannter Fehler");
        openPopup("eventplayererror", participantId);
      })
  }

  //Info Popup
  const [info, setInfo] = useState({
    lastName: '',
    firstName: '',
    participantGroupId: 0,
    roleSnapshot: null,
    canLead: false,
    priceOverridden: false,
    presenceStatus: 'Absent',
    phone: '',
    email: '',
    memo: '',
    playerMemo: '',
    price: null,
    group: 0,
    addOns: [],
    sentEmails: []
  });
  const infoGroupLabel = (() => {
    const { group, role } = resolveGroupAndRole(info, groupsById);
    return groupRoleLabel(group, role);
  })();
  const [editMemoValue, setEditMemoValue] = useState('');
  const [editIsOrganizing, setEditIsOrganizing] = useState(false);
  // Anmelde-Zusätze im Bearbeiten-Popup (R8): Mengen je Prozess-Zusatz beim Öffnen / aktuell, plus
  // Zeilen ohne aktuellen Prozess-Zusatz, die beim Übernehmen entfernt werden.
  const [editAddOnOriginal, setEditAddOnOriginal] = useState({});
  const [editAddOnQty, setEditAddOnQty] = useState({});
  const [editRemoveLineIds, setEditRemoveLineIds] = useState([]);
  // Saving state of the edit popup: it stays open until the update succeeded, so a rejected save (e.g. an add-on
  // booked out meanwhile) keeps every other edit and shows the reason inline.
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const editSessionRef = useRef(0); // +1 per opening of the edit popup
  const editOpenRef = useRef(false);
  editOpenRef.current = isPopupOpen && popupData.name === 'editeventplayer';

  const showPlayerInfo = () => {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (foundPost) setInfo(foundPost);
    openPopup('eventplayer', contextMenuData.identifier);
  };

  //Edit Popup
  const priceInputRef = useRef();
  // Originalpreis beim Popup-Öffnen, um manuelle Änderung zu erkennen.
  // info.price wird durch updatePrice() live verändert und taugt nicht als Vergleichsanker.
  const originalPriceRef = useRef(null);

  const handleEditEventPlayerConfirm = () => {
    if (editSaving || editAddOnOverStock) return;
    const session = editSessionRef.current;
    const stillEditing = () => editOpenRef.current && editSessionRef.current === session;
    setEditSaving(true);
    setEditError('');
    startSuccessBarLoading("Änderungen werden übernommen");
    const memo = editMemoValue;
    const priceRaw = priceInputRef.current?.value ?? '';
    const enteredPrice = priceRaw === '' ? null : Number(priceRaw);
    // Wenn der User den Preis NICHT manuell verändert hat, null senden —
    // damit das Backend bei einer Zusatz-Änderung den Preis automatisch neu berechnet.
    // (Backend übernimmt sonst den festen Wert und macht keinen Recalc.)
    const priceUnchanged = enteredPrice === (originalPriceRef.current ?? null);
    // Emptied field of a manual price = back to the standard price. The update treats a null price as
    // "unchanged", so an admin's reset is a separate call after the update (for Orgas the field is required).
    const restoreStandardPrice = enteredPrice === null && !!info.priceOverridden && isAdmin();
    // Zusätze nur mitsenden, wenn der Bereich geändert wurde (null = unverändert): geänderte Mengen der
    // Prozess-Zusätze (0 = entfernen) und zu entfernende Zeilen ohne Prozess-Zusatz. Bei Spieltagleitung sind
    // die Eingaben gesperrt — dann bleibt alles, wie es ist.
    const changedAddOns = editIsOrganizing ? [] : signupAddOns
      .map(a => ({ addOnId: a.addOnId, quantity: editAddOnQty[a.addOnId] ?? 0, original: editAddOnOriginal[a.addOnId] ?? 0 }))
      .filter(x => x.quantity !== x.original)
      .map(({ addOnId, quantity }) => ({ addOnId, quantity }));
    const removeLineIds = editIsOrganizing ? [] : editRemoveLineIds;
    const requestBody = {
      memo: memo,
      price: priceUnchanged ? null : enteredPrice,
      group: selectedGroup,
      isOrganizing: editIsOrganizing,
      addOns: changedAddOns.length > 0 ? changedAddOns : null,
      removeAddOnLineIds: removeLineIds.length > 0 ? removeLineIds : null
    };

    api.put(`/EventPlayer/${info.id}`, requestBody)
      .then((response) => {
        const updatedPosts = [...posts];
        const postIndex = updatedPosts.findIndex(post => post.id === info.id);
        if (postIndex >= 0) {
          updatedPosts[postIndex] = { ...updatedPosts[postIndex], ...response.data };
        }
        setMaxGroup(Math.max(0, ...updatedPosts.map(post => post.group ?? 0)));
        setPosts(updatedPosts);
        patchCachedParticipant(eventId, info.id, p => ({ ...p, ...response.data }));
        if (stillEditing()) closePopup();
        if (restoreStandardPrice) resetPrice(info.id);
        else startSuccessBarSuccess("Änderungen übernommen");
      })
      .catch(error => {
        closeSuccessBar();
        const data = error?.response?.data;
        if (stillEditing()) {
          // Inline, the popup stays open with every edit (e.g. 400 "nur noch 1 verfügbar" after a race).
          setEditError(typeof data === 'string' && data.trim()
            ? data
            : !error?.response ? 'Keine Verbindung – bitte später erneut versuchen.' : 'Änderungen konnten nicht übernommen werden.');
          return;
        }
        setError(data ?? "Unbekannter Fehler");
        openPopup("eventplayererror", info.id);
      })
      .finally(() => { if (editSessionRef.current === session) setEditSaving(false); });
  };

  const updatePrice = (event) => {
    const newValue = event.target.value;
    setInfo((prevInfo) => ({
      ...prevInfo,
      price: newValue === '' ? null : Number(newValue)
    }));
  };

  // ── Artikel-Buchung (Zusätze im Event, Kanal OnSite) ──
  // Buchbar ist, was das Event-Detail als „im Event buchbar“ liefert (Katalog live am Spielort, Preis je Gruppe) —
  // kein eigener Request. Bestehende Zeilen zeigen ihren eigenen Einzelpreis; Zeilen von Zusätzen, die hier nicht
  // (mehr) buchbar sind, lassen sich nur noch verringern.
  const [articleBusy, setArticleBusy] = useState(false);
  const [articleError, setArticleError] = useState('');

  function openArticlesBooking() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (foundPost) setInfo(foundPost);
    setArticleError('');
    setArticleBusy(false);
    openPopup('articlesbooking', contextMenuData.identifier);
  }

  function applyArticleResponse(res) {
    const updated = res.data;
    if (!updated?.id) return;
    setInfo(prev => (prev.id === updated.id ? { ...prev, ...updated } : prev));
    setPosts(prev => prev.map(p => (p.id === updated.id ? { ...p, ...updated } : p)));
    // Offline-Kopie des Spieltagleiters aktuell halten (Gesamtbetrag, Warenkorb-Symbol).
    patchCachedParticipant(eventId, updated.id, p => ({ ...p, ...updated }));
  }

  // Fehler bleiben im Popup sichtbar (ausgebucht, nicht anwesend …), statt es durch das Fehler-Popup zu ersetzen.
  function changeArticle(request) {
    if (articleBusy) return;
    setArticleBusy(true);
    setArticleError('');
    request()
      .then(applyArticleResponse)
      .catch(err => {
        const data = err?.response?.data;
        setArticleError(typeof data === 'string' && data.trim()
          ? data
          : !err?.response ? 'Keine Verbindung – bitte später erneut versuchen.' : 'Änderung konnte nicht gespeichert werden.');
      })
      .finally(() => setArticleBusy(false));
  }

  const addArticle = (addOnId) => changeArticle(() => api.post(`/EventPlayer/${info.id}/AddOns/${addOnId}`));
  const removeArticle = (addOnId) => changeArticle(() => api.delete(`/EventPlayer/${info.id}/AddOns/${addOnId}`));
  // Zeilen ohne (buchbaren) Katalog-Eintrag bzw. weitere Zeilen desselben Zusatzes — Änderung per Zeilen-Id.
  const decrementArticleLine = (lineId) => changeArticle(() => api.delete(`/EventPlayer/${info.id}/AddOns/ByLineId/${lineId}`));

  // Zeilen des Artikel-Popups: je buchbarem Zusatz eine Zeile (mit der ersten OnSite-Zeile des Zusatzes), danach
  // alle übrigen OnSite-Zeilen (Katalog-Eintrag gelöscht, hier nicht mehr buchbar, doppelte Zeilen alter Daten).
  const articleLines = onSiteLines(info);
  const shownArticleLineIds = new Set();
  const bookableArticleRows = onSiteAddOns.map(addOn => {
    const line = articleLines.find(l => sameAddOnId(l.addOnId, addOn.addOnId)) ?? null;
    if (line) shownArticleLineIds.add(line.id);
    return { addOn, line };
  });
  const otherArticleLines = articleLines.filter(l => !shownArticleLineIds.has(l.id));
  const articleBookable = isPresent(info);

  //Context menu
  const { contextMenuData } = useContextMenu();

  function callPlayer() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (foundPost?.phone) window.location.href = `tel:${foundPost.phone}`;
  }

  function emailPlayer() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (foundPost) navigate(ROUTES.mail(eventId, foundPost.playerId));
  }

  function editPlayer() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (foundPost) {
      setInfo(foundPost);
      setSelectedGroup(foundPost.group ?? 0);
      setEditMemoValue(foundPost.memo ?? '');
      setEditIsOrganizing(!!foundPost.isOrganizing);
      const quantities = signupQuantities(foundPost);
      setEditAddOnOriginal(quantities);
      setEditAddOnQty(quantities);
      setEditRemoveLineIds([]);
      editSessionRef.current += 1;
      setEditSaving(false);
      setEditError('');
      // Original-Preis für Vergleich beim Submit merken (info.price wird durch
      // updatePrice live verändert und ist als Vergleichsanker ungeeignet).
      originalPriceRef.current = foundPost.price ?? null;
    }
    openPopup('editeventplayer', contextMenuData.identifier);
  }

  function openPlayerWindowFromContextMenu() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (foundPost) openPlayerWindow(foundPost.playerId);
  }

  function openPlayerWindowFromPopup() {
    closePopup();
    const foundPost = posts.find(post => post.id === popupData.identifier);
    if (foundPost) openPlayerWindow(foundPost.playerId);
  }

  function openPlayerWindow(playerId) {
    navigate(ROUTES.user(playerId));
  }

  function removeParticipant() {
    openPopup("confirmunregistereventplayer", contextMenuData.identifier);
  }

  function convertWaitlistToPlayer() {
    startSuccessBarLoading("Änderungen werden übernommen");
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (!foundPost) return;

    // Response: { participant, reducedAddOns } — Zusätze ohne Warteliste werden auf den Restbestand gekürzt.
    api.put(`/EventPlayer/${foundPost.id}/Promote`)
      .then((response) => {
        const participant = response.data?.participant ?? null;
        startSuccessBarSuccess(withReductions("Spieler hinzugefügt", response.data?.reducedAddOns));
        if (!participant) return;
        const updatedPosts = [...posts];
        const postIndex = updatedPosts.findIndex(post => post.id === foundPost.id);
        if (postIndex >= 0) {
          updatedPosts[postIndex] = { ...updatedPosts[postIndex], ...participant };
        }
        setPosts(updatedPosts);
      })
      .catch(error => {
        closeSuccessBar();
        setError(error?.response?.data ?? "Unbekannter Fehler");
        openPopup("eventplayererror", foundPost.id);
      })
  }

  function removeWaitlist() {
    openPopup("confirmunregisterwaitlist", contextMenuData.identifier);
  }

  function sendSpotAvailableMail() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (!foundPost) return;
    startSuccessBarLoading("Platz-Frei Mail wird in Versand-Queue gestellt");

    api.post(`/EventPlayer/${foundPost.id}/SendSpotAvailable`)
      .then((response) => {
        startSuccessBarSuccess("Platz-Frei Mail wird im Hintergrund versendet");
        const updatedPosts = [...posts];
        const postIndex = updatedPosts.findIndex(p => p.id === foundPost.id);
        if (postIndex >= 0) {
          const merged = { ...updatedPosts[postIndex], ...response.data };
          // Der EventPlayerSentEmail-DB-Record entsteht erst asynchron im SendMailConsumer.
          // Damit das WaitlistStatusIcon sofort auf "gesendet" wechselt, injizieren wir
          // einen optimistischen Eintrag – sofern die API noch keinen zurückgegeben hat.
          const alreadyTracked = (merged.sentEmails ?? []).some(isSpotAvailableMail);
          if (!alreadyTracked) {
            merged.sentEmails = [
              ...(merged.sentEmails ?? []),
              { id: -1, subject: null, sentAt: new Date().toISOString(), textblockName: null, slot: SPOT_AVAILABLE_SLOT }
            ];
          }
          updatedPosts[postIndex] = merged;
        }
        setPosts(updatedPosts);
      })
      .catch(error => {
        closeSuccessBar();
        // 404 without text: no recipient (no e-mail address), or the mail is switched off for the group or
        // has no textblock assigned (no code default).
        setError(error?.response?.status === 404 && !error?.response?.data
          ? `Keine Mail verschickt: Der Spieler hat keine E-Mail-Adresse oder die Mail „${mailSlotLabel(SPOT_AVAILABLE_SLOT)}“ ist für seine Gruppe ausgeschaltet bzw. ohne Textbaustein.`
          : (error?.response?.data ?? "Unbekannter Fehler"));
        openPopup("eventplayererror", foundPost.id);
      })
  }

  // Admin: discard a manually set price — the backend recalculates the standard price of the snapshot group.
  function resetPrice(participantId) {
    startSuccessBarLoading("Standardpreis wird wiederhergestellt");
    api.put(`/EventPlayer/${participantId}/ResetPrice`)
      .then((response) => {
        const updated = response.data;
        startSuccessBarSuccess(`Standardpreis wiederhergestellt: ${formatEuro(updated?.price ?? 0)}`);
        setPosts(prev => prev.map(p => (p.id === participantId ? { ...p, ...updated } : p)));
        patchCachedParticipant(eventId, participantId, p => ({ ...p, ...updated }));
        // Edit popup of this row still open: show the new price and use it as comparison anchor, otherwise
        // "Übernehmen" would send it back as a manual price.
        if (info.id === participantId) {
          setInfo(prev => ({ ...prev, ...updated }));
          originalPriceRef.current = updated?.price ?? null;
        }
      })
      .catch(error => {
        closeSuccessBar();
        setError(error?.response?.data ?? "Unbekannter Fehler");
        openPopup("eventplayererror", participantId);
      });
  }

  function resetPriceFromContextMenu() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (foundPost) resetPrice(foundPost.id);
  }

  // Bezahlt-Status (manuell). Kein Datums-Hinweis wie bei der Anwesenheit — Vorkasse ist jederzeit möglich.
  const [sendPaidMail, setSendPaidMail] = useState(true);
  const contextPost = posts.find(post => post.id === contextMenuData.identifier) ?? null;
  const canMarkPaid = !!contextPost && !contextPost.isPaid && owesPayment(contextPost);
  // Zurücknehmen geht immer, wenn markiert ist (auch wenn der Preis inzwischen auf 0 steht).
  const canUnmarkPaid = !!contextPost && !!contextPost.isPaid;

  function markPaid() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (!foundPost) return;
    // Vorkasse-Event: Rückfrage mit optionaler Zahlungsbestätigung (Mail-Slot payment_confirmed).
    if (rules.prepaymentRequired) {
      setInfo(foundPost);
      setSendPaidMail(!!foundPost.email);
      openPopup('confirmpaidprivate', foundPost.id);
      return;
    }
    setPaid(foundPost.id, true, false);
  }

  function unmarkPaid() {
    const foundPost = posts.find(post => post.id === contextMenuData.identifier);
    if (foundPost) setPaid(foundPost.id, false, false);
  }

  const handleConfirmPaidPrivate = () => {
    setPaid(info.id, true, sendPaidMail && !!info.email);
  };

  function setPaid(participantId, isPaid, sendConfirmationMail) {
    startSuccessBarLoading(isPaid ? "Wird als bezahlt markiert" : "Bezahlung wird zurückgenommen");
    api.put(`/EventPlayer/${participantId}/Paid`, { isPaid, sendConfirmationMail })
      .then((response) => {
        startSuccessBarSuccess(!isPaid
          ? "Bezahlung zurückgenommen"
          : sendConfirmationMail ? "Als bezahlt markiert – Bestätigungsmail wird versendet" : "Als bezahlt markiert");
        const updated = response.data;
        setPosts(prev => prev.map(p => (p.id === participantId ? { ...p, ...updated } : p)));
        // Offline-Kopie des Spieltagleiters aktuell halten (sonst falsche Farben nach Offline-Reload).
        patchCachedParticipant(eventId, participantId, p => ({ ...p, ...updated }));
      })
      .catch(error => {
        closeSuccessBar();
        setError(error?.response?.data ?? "Unbekannter Fehler");
        openPopup("eventplayererror", participantId);
      });
  }

  // ── Bearbeiten-Popup: Anmelde-Zusätze ──
  const signupAddOnIds = new Set(signupAddOns.map(a => Number(a.addOnId)));
  // Anmelde-Zeilen ohne aktuellen Prozess-Zusatz (Katalog-Eintrag gelöscht, aus dem Prozess genommen): nur lesen,
  // gezielt entfernbar.
  const editOrphanLines = signupLines(info).filter(l => l.addOnId == null || !signupAddOnIds.has(Number(l.addOnId)));
  const editShowAddOns = signupAddOns.length > 0 || editOrphanLines.length > 0;
  // Nur bestätigte (nicht Warteliste) Teilnehmer zählen gegen das Limit — nur für sie gilt der Restbestand.
  const editCountsCapacity = !isWaitlist(info);

  // Restbestand eines Zusatzes für diesen Teilnehmer (R8): others = alles außer seinen eigenen Anmelde-Zeilen
  // (andere Teilnehmer + eigene Artikel-Zeilen desselben Zusatzes), free = Höchstmenge, die er haben kann.
  // null = unbegrenzt bzw. ohne Limit-Prüfung (Warteliste).
  const editCapacityFor = (addOn) => {
    const max = Number(addOn.maxQuantity) || 0;
    if (!editCountsCapacity || max === 0) return null;
    const ownOnSite = onSiteLines(info)
      .filter(l => sameAddOnId(l.addOnId, addOn.addOnId))
      .reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
    const others = taken(posts, addOn.addOnId, info.id) + ownOnSite;
    return { max, others, free: available(max, others) };
  };

  // Zweite Zeile je Zusatz: Preis für diesen Teilnehmer (bestehende Zeile: ihr eigener Einzelpreis), Menge je
  // Person und Restbestand.
  const editAddOnHint = (addOn, quantity) => {
    const line = signupLines(info).find(l => sameAddOnId(l.addOnId, addOn.addOnId));
    const unitPrice = line
      ? Number(line.unitPrice ?? 0)
      : signupUnitPrice({ price: addOn.signupPrice, participantGroupPrices: addOn.signupParticipantGroupPrices }, info.participantGroupId, editIsOrganizing);
    const multi = Number(addOn.maxPerPerson) > 1;
    const parts = [`${formatAddOnPrice(unitPrice)}${multi && unitPrice > 0 ? ' je Stück' : ''}`];
    if (multi) parts.push(`bis ${addOn.maxPerPerson} je Person`);
    const capacity = editCapacityFor(addOn);
    if (!capacity) return parts.join(' · ');
    const { max, others, free } = capacity;
    const original = editAddOnOriginal[addOn.addOnId] ?? 0;
    let state;
    if (quantity > original && quantity > free) {
      // Same wording as the 400 of the backend — the save would be rejected.
      state = <span className="eventAddOnHintWarn">nur noch {free} verfügbar (Limit: {max})</span>;
    } else if (free - quantity > 0) {
      state = `noch ${free - quantity} frei`;
    } else {
      const label = `ausgebucht (${others + quantity}/${max})`;
      state = quantity === 0 ? <span className="eventAddOnHintWarn">{label}</span> : label;
    }
    return <>{parts.join(' · ')} · {state}</>;
  };

  // Ausgebucht und (noch) nicht gebucht: nichts zu ändern.
  const editAddOnLocked = (addOn, quantity) =>
    quantity === 0 && (editAddOnOriginal[addOn.addOnId] ?? 0) === 0 && editCapacityFor(addOn)?.free === 0;

  // Obergrenze des „+“ (nur bestätigte Teilnehmer mit Limit): Restbestand, eine bestehende höhere Menge darf
  // bleiben (max(Original, frei)). null = nur maxPerPerson.
  const editAddOnMax = (addOn) => {
    const capacity = editCapacityFor(addOn);
    if (!capacity) return null;
    return Math.max(editAddOnOriginal[addOn.addOnId] ?? 0, capacity.free);
  };

  // Mehr als der Restbestand (z. B. weil andere inzwischen gebucht haben): das Backend lehnt ab (400) — „Übernehmen“
  // bleibt gesperrt, der Hinweis der Zeile nennt den Restbestand. Bei Spieltagleitung werden keine Zusätze gesendet.
  const editAddOnOverStock = !editIsOrganizing && signupAddOns.some(addOn => {
    const capacity = editCapacityFor(addOn);
    if (!capacity) return false;
    const quantity = editAddOnQty[addOn.addOnId] ?? 0;
    return quantity > (editAddOnOriginal[addOn.addOnId] ?? 0) && quantity > capacity.free;
  });

  const toggleOrphanRemoval = (lineId) => setEditRemoveLineIds(prev =>
    (prev.includes(lineId) ? prev.filter(id => id !== lineId) : [...prev, lineId]));

  const editAddOnNote = editIsOrganizing
    ? 'Spieltagleiter bekommen keine Anmelde-Zusätze.'
    : keepsPrice(info)
      ? 'Preis bleibt unverändert (bezahlt/manuell) – Zusatz wird nicht berechnet.'
      : null;

  // ── Zähler der Anmelde-Zusätze (Kopfzeile) ──
  const addOnCounters = signupAddOns.map(addOn => {
    const used = takenById.get(Number(addOn.addOnId)) ?? 0;
    const max = Number(addOn.maxQuantity) || 0;
    const exhausted = isExhausted(max, used);
    return {
      addOn,
      value: formatCapacity(used, max),
      exhausted,
      // Catalog color tints the pill (AddOnChips.css); exhausted stays the yellow warning pill.
      colored: !exhausted && hasAddOnColor(addOn.colorKey),
      tooltip: max > 0
        ? `${addOn.name}: ${used} von ${max} gebucht${used >= max ? ' – ausgebucht' : ''}`
        : `${addOn.name}: ${used} gebucht (unbegrenzt)`,
    };
  });

  return (
    <React.Fragment>
      <main className={posts.length > 0 ? "eventColumnMain hasPosts" : "eventColumnMain"}>
        <EventOfflineStatus isLeader={offlineStatus.isLeader} noCache={offlineStatus.noCache} fetchedAt={offlineStatus.fetchedAt} />
        {posts.length > 0 && (
          <div className={"eventTopBar"}>
            <div className={"registrationsFilterMeta"}>
              <Tooltip text={'Eingetragene Spieler (bestätigt + Warteliste)'} tapToShow>
                <span className={"registrationsFilterChip"}>
                  <Icon className={"registrationsFilterChipIcon"} icon={'group'} />
                  {confirmedCount}{maxPlayers > 0 ? ` / ${maxPlayers}` : ''}
                  {waitlistCount > 0 && <span className={"eventStatsWaitlist"}> +{waitlistCount}</span>}
                </span>
              </Tooltip>
              {/* Je Anmelde-Zusatz des Prozesses ein Zähler „Leihausrüstung 5/8“ (alle Kanäle, ohne Warteliste). */}
              {addOnCounters.map(c => (
                <Tooltip key={c.addOn.addOnId} text={c.tooltip} tapToShow className={"eventAddOnCounterTip"}>
                  <span
                    className={`registrationsFilterChip eventAddOnCounter${c.exhausted ? ' isExhausted' : ''}${c.colored ? ' isAddOnColored' : ''}`}
                    style={c.colored ? addOnColorStyle(c.addOn.colorKey) : undefined}
                  >
                    <Icon className={"registrationsFilterChipIcon"} icon={'inventory_2'} />
                    <span className={"eventAddOnCounterName"}>{c.addOn.name}</span>
                    <span className={"eventAddOnCounterValue"}>{c.value}</span>
                  </span>
                </Tooltip>
              ))}
              {openPayments.count > 0 && (
                <Tooltip text={'Offene Zahlungen: zahlungspflichtig, noch nicht bezahlt, nicht abwesend'} tapToShow>
                  <span className={"registrationsFilterChip negative"}>
                    <Icon className={"registrationsFilterChipIcon"} icon={'payments'} />
                    offen: {openPayments.count} · {formatEuro(openPayments.cents / 100)}
                  </span>
                </Tooltip>
              )}
              {isAdmin() && rules.linkSignup && <PrivateLinkChip eventId={eventId} />}
              {isAdmin() && pendingCount != null && pendingCount > 0 && (
                <Tooltip text={'Offene Anmeldungen'} tapToShow>
                  <span className={"registrationsFilterChip accent"}>
                    <Icon className={"registrationsFilterChipIcon"} icon={'how_to_reg'} />
                    {pendingCount}
                  </span>
                </Tooltip>
              )}
            </div>
            <Textfield
              icon={'search'}
              placeholder={'Vorname oder Nachname'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
        <ListHeader gridTemplateColumns={'52px 1fr 1fr 52px 1fr 52px'} className={'eventCard'}>
          <ListTextItem isHeader>Gruppe</ListTextItem>
          <ListTextItem isHeader>Nachname</ListTextItem>
          <ListTextItem isHeader>Vorname</ListTextItem>
          <ListTextItem isHeader>{signupAddOns.length > 0 ? 'Zusätze' : ''}</ListTextItem>
          <ListTextItem isHeader>Preis</ListTextItem>
          <ListTextItem isHeader>Anwesenheit</ListTextItem>
        </ListHeader>
        {confirmedDisplayPosts.map((post, index) => (
          <ListItem
            key={post.id}
            identifier={post.id}
            gridTemplateColumns={'52px 1fr 1fr 52px 1fr 52px'}
            memo={post.memo}
            contextMenuIdentifier='eventplayer'
            index={index}
            onDoubleClick={handleListItemClick}
            className={'eventCard'}
          >
            <ParticipantGroupDot row={post} size='md' />
            <ListTextItem>{post.lastName ?? 'N/A'}</ListTextItem>
            <ListTextItem>{post.firstName ?? 'N/A'}</ListTextItem>
            <SignupAddOnChips post={post} />
            <ListTextItem>
              <PriceAmount post={post} />
              <GroupRoleText post={post} groupsById={groupsById} />
            </ListTextItem>
            <Checkbox className={"playerPresent"} identifier={post.id} selectedState={presenceToCheckboxState(post.presenceStatus)} />
            <ListSymbols>
              <ListSymbol icon={"visibility_off"} visible={!!post.isAnonymized} type={1} />
              <ListSymbol icon={"sports"} visible={!!post.isOrganizing} type={1} />
              <ListSymbol icon={"note_stack"} visible={!!post.memo} type={0} />
              <ListSymbol icon={"note_stack"} visible={!!post.playerMemo} type={2} />
              <ListSymbol icon={"send"} visible={hasPendingMail(post)} type={1} tooltip={PENDING_MAIL_TOOLTIP} />
              <ListSymbol icon={"shopping_cart"} visible={onSiteLines(post).length > 0} type={0} />
              <ListSymbol icon={"cloud_upload"} visible={pendingIds.has(post.id)} type={2} />
              <ListSymbol icon={"error"} visible={failedIds.has(post.id)} type={1} />
            </ListSymbols>
            {post.group > 0 && <ListColorExtension group={post.group} />}
          </ListItem>
        ))}
      </main>
      {hasWaitlistPlayers && (
        <main className="waitlistSectionMain">
          <button
            type="button"
            className={`waitlistSectionToggle ${waitlistExpanded ? 'expanded' : ''}`}
            onClick={() => setWaitlistExpanded(e => !e)}
          >
            <Icon className="waitlistSectionChevron" icon={waitlistExpanded ? 'expand_more' : 'chevron_right'} />
            <span className="waitlistSectionTitle">Warteliste</span>
            <span className="waitlistSectionCount">{waitlistCount}</span>
          </button>
          {waitlistExpanded && (
            <div className="waitlistSectionList">
              {waitlistDisplayPosts.map((post, index) => (
                <ListItem
                  key={post.id}
                  identifier={post.id}
                  gridTemplateColumns={'52px 1fr 1fr 52px 1fr 52px'}
                  memo={""}
                  contextMenuIdentifier='waitlistplayer'
                  index={index}
                  className={'eventCard waitlist'}
                >
                  <ParticipantGroupDot row={post} size='md' />
                  <ListTextItem type={1}>{post.lastName ?? 'N/A'}</ListTextItem>
                  <ListTextItem type={1}>{post.firstName ?? 'N/A'}</ListTextItem>
                  <SignupAddOnChips post={post} />
                  <ListTextItem type={1}>
                    <GroupRoleText post={post} groupsById={groupsById} />
                  </ListTextItem>
                  <div className="listTextContainer"></div>
                  <ListSymbols>
                    <WaitlistStatusIcon sentAt={getSpotMailSentAt(post)} tooltip={spotMailTooltip(getSpotMailSentAt(post))} />
                    <ListSymbol icon={"visibility_off"} visible={!!post.isAnonymized} type={1} />
                    <ListSymbol icon={"sports"} visible={!!post.isOrganizing} type={1} />
                    <ListSymbol icon={"note_stack"} visible={!!post.memo} type={0} />
                    <ListSymbol icon={"note_stack"} visible={!!post.playerMemo} type={2} />
                    <ListSymbol icon={"send"} visible={hasPendingMail(post)} type={1} tooltip={PENDING_MAIL_TOOLTIP} />
                  </ListSymbols>
                  {post.group > 0 && <ListColorExtension group={post.group} />}
                </ListItem>
              ))}
            </div>
          )}
        </main>
      )}
      <ContextMenu type={'eventplayer'}>
        <ContextMenuItem label={'Anrufen'} icon={'call'} type={2} onClick={callPlayer} />
        {isAdmin() && <ContextMenuItem label={'E-Mail'} icon={'mail'} type={3} onClick={emailPlayer} disabled={isOffline} />}
        <ContextMenuSeparator />
        <ContextMenuItem label={'Info'} icon={'question_mark'} type={0} onClick={showPlayerInfo} />
        <ContextMenuItem label={'Bearbeiten'} icon={'edit'} type={0} onClick={editPlayer} disabled={isOffline} />
        {isAdmin() && <ContextMenuItem label={'Stammdaten'} icon={'folder_open'} type={0} onClick={openPlayerWindowFromContextMenu} disabled={isOffline} />}
        <ContextMenuSeparator />
        <ContextMenuItem label={'Zurücksetzen'} icon={'history'} type={0} onClick={resetPlayerPresence} />
        {isOrgaOrAdmin() && <ContextMenuSeparator />}
        {isOrgaOrAdmin() && <ContextMenuItem label={'Artikel'} icon={'shopping_cart'} type={0} onClick={openArticlesBooking} disabled={isOffline} />}
        {isOrgaOrAdmin() && canMarkPaid && <ContextMenuItem label={'Als bezahlt markieren'} icon={'paid'} type={2} onClick={markPaid} disabled={isOffline} />}
        {isOrgaOrAdmin() && canUnmarkPaid && <ContextMenuItem label={'Bezahlung zurücknehmen'} icon={'money_off'} type={0} onClick={unmarkPaid} disabled={isOffline} />}
        {isAdmin() && contextPost?.priceOverridden && <ContextMenuItem label={'Standardpreis wiederherstellen'} icon={'restart_alt'} type={0} onClick={resetPriceFromContextMenu} disabled={isOffline} />}
        {isAdmin() && <ContextMenuSeparator />}
        {isAdmin() && <ContextMenuItem label={'Abmelden'} icon={'delete_sweep'} type={1} onClick={removeParticipant} disabled={isOffline} />}
      </ContextMenu>
      <ContextMenu type={'waitlistplayer'}>
        {isAdmin() && <ContextMenuItem label={'E-Mail'} icon={'mail'} type={3} onClick={emailPlayer} disabled={isOffline} />}
        {isAdmin() && <ContextMenuItem label={'Platz Frei'} icon={'hourglass_empty'} type={3} onClick={sendSpotAvailableMail} disabled={isOffline} />}
        {isAdmin() && <ContextMenuItem label={'Stammdaten'} icon={'folder_open'} type={0} onClick={openPlayerWindowFromContextMenu} disabled={isOffline} />}
        {isAdmin() && <ContextMenuItem label={'Hinzufügen'} icon={'add'} type={2} onClick={convertWaitlistToPlayer} disabled={isOffline} />}
        <ContextMenuSeparator />
        <ContextMenuItem label={'Info'} icon={'question_mark'} type={0} onClick={showPlayerInfo} />
        <ContextMenuItem label={'Bearbeiten'} icon={'edit'} type={0} onClick={editPlayer} disabled={isOffline} />
        {isAdmin() && <ContextMenuSeparator />}
        {isAdmin() && <ContextMenuItem label={'Abmelden'} icon={'delete_sweep'} type={1} onClick={removeWaitlist} disabled={isOffline} />}
      </ContextMenu>
      <Popup name='confirmunregistereventplayer' confirmText='Abmelden' headerText='Aktion bestätigen'
        type="confirmaction" onConfirm={handleConfirmUnregister} withCancel
      >
        <p>Du bist dabei, den gewählten Spieler vom Spieltag abzumelden.</p>
        <p>Soll der Spieler zusätzlich die Mail „{mailSlotLabel('cancelled')}“ erhalten? Sie geht nur raus, wenn sie für seine Gruppe eingeschaltet ist und eine E-Mail-Adresse hinterlegt ist.</p>
        <CustomComboBox selected={selectedSendUnregisterMailStatus} onStatusChange={setSelectedSendUnregisterMailStatus}>
          <StatusDot type='yesno' statusId={0} />
          <StatusDot type='yesno' statusId={1} />
        </CustomComboBox>
      </Popup>
      <Popup name='confirmunregisterwaitlist' confirmText='Abmelden' headerText='Aktion bestätigen'
        type="confirmaction" onConfirm={handleConfirmUnregister} withCancel
      >
        <p>Bist du sicher, dass du den Wartelisteneintrag von diesem Event abmelden willst?</p>
      </Popup>
      <Popup name='warningchangeeventplayer' confirmText='Bestätigen' headerText='Warnung'
        type="warning" onConfirm={handleChangePlayerPresence} withCancel
      >
        <p>Der Spieltag liegt in der Vergangenheit oder in der Zukunft. Möchtest du trotzdem fortfahren?</p>
      </Popup>
      <Popup name='eventplayer' confirmText='Ok' headerText='Info' type="info">
        <div className="infoPopupBody">
          <p className="infoPopupName">{info.firstName} {info.lastName}</p>
          {infoGroupLabel && (
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Gruppe am Spieltag</span>
              <span className="infoPopupValue infoPopupGroup">
                <ParticipantGroupDot row={info} size='md' withTooltip={false} />
                {infoGroupLabel}
              </span>
            </div>
          )}
          {isWaitlist(info) && info.createdAt && (
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Auf Warteliste seit</span>
              <span className="infoPopupValue">
                {(() => {
                  const d = new Date(info.createdAt);
                  return d.toLocaleDateString('de-DE') + ' '
                    + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                })()}
              </span>
            </div>
          )}
          {info.playerMemo && (
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Spielermemo</span>
              <p className="popupReadOnly">{info.playerMemo}</p>
            </div>
          )}
          {(info.memo ?? '') && (
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Memo</span>
              <div className="memoOutput">
                {(info.memo ?? '').split('\\n').map((zeile, index) => (
                  index % 2 === 0 ? <h4 key={index}>{zeile}</h4> : <p key={index}>{zeile}</p>
                ))}
              </div>
            </div>
          )}
          <div className="infoPopupSection">
            <span className="infoPopupLabel">Preis</span>
            <span className="infoPopupValue">
              {info.price == null && onSiteLines(info).length === 0
                ? 'Standard'
                : formatEuro(getPlayerTotal(info))}
              {info.priceOverridden && <span className="infoPopupValueMeta"> · manuell gesetzt</span>}
            </span>
          </div>
          {signupLines(info).length > 0 && (
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Zusätze</span>
              <div className="infoPopupArticleList">
                {signupLines(info).map(l => (
                  <span key={l.id} className="infoPopupArticleRow">
                    <span>{formatAddOnQty(l.name, l.quantity)}</span>
                    <span>{formatEuro((Number(l.unitPrice) || 0) * (Number(l.quantity) || 0))}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {(info.isPaid || (owesPayment(info) && !isWaitlist(info))) && (
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Bezahlung</span>
              {info.isPaid ? (
                <span className="infoPopupValue payStateText paid">
                  {info.paidByPresence ? 'Bezahlt (automatisch bei Anwesenheit)' : 'Bezahlt (manuell)'}
                  {info.paidAt && <span className="payStateMeta"> · {formatDateTimeDe(info.paidAt)}</span>}
                </span>
              ) : paymentState(info) === 'open' ? (
                <span className="infoPopupValue payStateText open">Offen – {formatEuro(info.price)}</span>
              ) : (
                <span className="infoPopupValue payStateText none">Nicht bezahlt – abwesend, zählt nicht als offen</span>
              )}
            </div>
          )}
          {onSiteLines(info).length > 0 && (
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Artikel</span>
              <div className="infoPopupArticleList">
                {onSiteLines(info).map(l => (
                  <span key={l.id} className="infoPopupArticleRow">
                    <span>{formatAddOnQty(l.name, l.quantity)}</span>
                    <span>{formatEuro((Number(l.unitPrice) || 0) * (Number(l.quantity) || 0))}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {(info.sentEmails ?? []).length > 0 && (
            <div className="infoPopupSection">
              <span className="infoPopupLabel">E-Mails</span>
              <div className="infoPopupArticleList">
                {(info.sentEmails ?? []).map(mail => (
                  <span key={mail.id} className="infoPopupArticleRow">
                    <span>{mail.slot ? mailSlotLabel(mail.slot) : (mail.subject || 'E-Mail')}</span>
                    <span className="payStateMeta">{formatDateTimeDe(mail.sentAt)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {hasPendingMail(info) && (
            <p className="payConfirmHint">Die Statusmail für diesen Spieler wurde noch nicht versendet.</p>
          )}
        </div>
      </Popup>
      <Popup
        name='editeventplayer'
        confirmText={'Übernehmen'}
        onConfirm={handleEditEventPlayerConfirm}
        withCancel
        type="info"
        dontCloseOnConfirm
        confirmDisabled={editSaving || editAddOnOverStock}
      >
        <h3>Memo</h3>
        <PopupDescriptionItem>
          Hierbei handelt es sich im eine Notiz für den ausgewählten Spieltag und ausschließlich für den ausgewählten Spieltag. Du kannst eine dauerhafte Notiz in den
          <PopupDescriptionLinkItem onClick={openPlayerWindowFromPopup}> Spielerstammdaten </PopupDescriptionLinkItem>
          setzen. Diese ist dann an keinen Spieltag, sondern nur an den Spieler gebunden.
        </PopupDescriptionItem>
        <textarea className={"memoInput"} maxLength={200} placeholder='Memoeintrag hinzufügen' value={editMemoValue} onChange={e => setEditMemoValue(e.target.value)}></textarea>
        <h3>Preis</h3>
        {/* Only admins may restore the standard price (empty field / button) — for Orgas a manual price
            cannot be cleared, an empty field would silently keep it. */}
        <input type='number' ref={priceInputRef} className={"priceInput"} min={0} max={10000} step={0.5} placeholder='Standard' value={info.price == null ? "" : info.price} onChange={updatePrice} required={!!info.priceOverridden && !isAdmin()} />
        {info.priceOverridden ? (
          <div className="priceOverrideHint">
            <p className="formFieldHint">
              Manuell gesetzter Preis – er bleibt bei Änderungen (z. B. Zusätze, Spieltagspreise) unverändert.
              {isAdmin()
                ? ' Leeres Feld stellt den Standardpreis wieder her.'
                : ' Den Standardpreis kann nur ein Admin wiederherstellen.'}
            </p>
            {isAdmin() && (
              <button type="button" className="priceOverrideReset" onClick={() => resetPrice(info.id)} disabled={isOffline}>
                <Icon className="priceOverrideResetIcon" icon="restart_alt" />
                Standardpreis wiederherstellen
              </button>
            )}
          </div>
        ) : (
          <p className="formFieldHint">Ein geänderter Preis gilt als manueller Preis und wird danach nicht mehr automatisch neu berechnet.</p>
        )}
        <h3>Team</h3>
        <CustomComboBox selected={selectedGroup} onStatusChange={setSelectedGroup}>
          <div></div>
          {
            Array.from({ length: maxGroup + 1 }, (_, index) => (
              <div key={index} className={`groupBadge group${index + 1}`}>{index + 1}</div>
            ))
          }
        </CustomComboBox>
        {/* Anmelde-Zusätze (Teil des Preises, zählen gegen das Limit). Zeilen von Zusätzen, die der Prozess nicht
            mehr anbietet, bleiben unangetastet, bis sie hier gezielt entfernt werden. */}
        {editShowAddOns && <>
          <h3>Zusätze</h3>
          {/* Two booking channels share the same add-ons — say which one this is (counterpart: article popup). */}
          <p className="formFieldHint">
            Bei der Anmeldung gebucht – Teil des Preises (offen/bezahlt). Was vor Ort gekauft wird, läuft über „Artikel“.
          </p>
          <EventAddOnInputs
            addOns={signupAddOns}
            values={editAddOnQty}
            onChange={(addOnId, quantity) => setEditAddOnQty(prev => ({ ...prev, [addOnId]: quantity }))}
            hintFor={editAddOnHint}
            disabledFor={editAddOnLocked}
            maxFor={editAddOnMax}
            disabled={editIsOrganizing}
            note={editAddOnNote}
          >
            {editOrphanLines.map(line => {
              const removed = editRemoveLineIds.includes(line.id);
              const amount = (Number(line.unitPrice) || 0) * (Number(line.quantity) || 0);
              return (
                <div key={line.id} className={`eventAddOnOrphan${removed ? ' isRemoved' : ''}`}>
                  <span className="eventAddOnOrphanText">
                    <span className="eventAddOnOrphanName">{formatAddOnQty(line.name, line.quantity)}</span>
                    <span className="eventAddOnOrphanHint">
                      {removed
                        ? 'Wird beim Übernehmen entfernt'
                        : `${formatAddOnPrice(amount)} · ${line.addOnId == null ? 'Katalog-Eintrag gelöscht' : 'nicht mehr im Prozess'}`}
                    </span>
                  </span>
                  <ActionButton
                    icon={removed ? 'undo' : 'delete'}
                    label={removed ? 'Behalten' : 'Entfernen'}
                    type={removed ? undefined : 1}
                    onClick={() => toggleOrphanRemoval(line.id)}
                    disabled={editIsOrganizing}
                  />
                </div>
              );
            })}
          </EventAddOnInputs>
        </>}
        {/* Leiten darf nur, wen die API als canLead meldet (Rolle am Spieltag, deren Rolleneinstellung
            „Kann Spieltage leiten“ an ist) — keine eigene Rollenprüfung im Client. Bestehende Leiter ohne
            diese Berechtigung behalten den Schalter, damit sie ausgetragen werden können. */}
        {(info.canLead || info.isOrganizing) && <>
          <h3>Spieltagleitung:</h3>
          <CustomTogglebox onChange={e => setEditIsOrganizing(e.target.checked)} checked={editIsOrganizing} placeholder={'Spieltagleiter'} />
          {!info.canLead && (
            <p className="formFieldHint">Am Spieltag keine Rolle, die Spieltage leiten darf – die Spieltagleitung kann nur noch ausgeschaltet werden.</p>
          )}
        </>}
        {/* Right above "Übernehmen": why the save was refused — the popup keeps every edit. */}
        {editError && <p className="editEventPlayerError" role="alert">{editError}</p>}
      </Popup>
      <Popup name='articlesbooking' headerText='Artikel buchen' confirmText='Schließen'>
        <p className="articleBookingPlayerName">
          {info.firstName} {info.lastName}
        </p>
        {/* Counterpart of the hint in the edit popup's "Zusätze" section. */}
        <p className="formFieldHint articleBookingHint">
          Vor Ort bezahlt – nicht Teil des Preises. Zusätze aus der Anmeldung stehen im Bearbeiten-Popup.
        </p>
        {!articleBookable && bookableArticleRows.length > 0 && (
          <p className="articleBookingNotice">
            <Icon className="articleBookingNoticeIcon" icon={'info'} />
            Nur für anwesende Spieler buchbar.
          </p>
        )}
        {articleError && <p className="articleBookingError" role="alert">{articleError}</p>}
        {bookableArticleRows.length === 0 && otherArticleLines.length === 0 && (
          <p className="articleBookingEmpty">Keine im Event buchbaren Zusätze vorhanden.</p>
        )}
        {bookableArticleRows.map(({ addOn, line }) => {
          const qty = Number(line?.quantity ?? 0);
          // Bestehende Zeile: ihr eingefrorener Einzelpreis; sonst der aktuelle Preis für die Gruppe des Spielers.
          const unitPrice = line
            ? Number(line.unitPrice ?? 0)
            : (priceFor({ price: addOn.onSitePrice, participantGroupPrices: addOn.onSiteParticipantGroupPrices }, info.participantGroupId) ?? 0);
          const max = Number(addOn.maxQuantity) || 0;
          const used = takenById.get(Number(addOn.addOnId)) ?? 0;
          const exhausted = isExhausted(max, used);
          const meta = max === 0 ? null : exhausted ? `ausgebucht ${used}/${max}` : `noch ${available(max, used)}`;
          return (
            <div key={`addon-${addOn.addOnId}`} className="articleBookingRow">
              <span className="articleBookingText">
                <span className="articleBookingName">{addOn.name}</span>
                {meta && <span className={`articleBookingMeta${exhausted ? ' isWarning' : ''}`}>{meta}</span>}
              </span>
              <span className="articleBookingPrice">{formatEuro(unitPrice)}</span>
              <div className="articleBookingControls">
                <button type="button" className="articleBookingBtn minus" disabled={qty === 0 || articleBusy}
                  onClick={() => removeArticle(addOn.addOnId)} aria-label={`${addOn.name}: eins weniger`}>−</button>
                <span className="articleBookingQty">{qty}</span>
                <button type="button" className="articleBookingBtn plus" disabled={!articleBookable || exhausted || articleBusy}
                  onClick={() => addArticle(addOn.addOnId)} aria-label={`${addOn.name}: eins mehr`}>+</button>
              </div>
            </div>
          );
        })}
        {/* Zeilen ohne buchbaren Zusatz hier (Katalog-Eintrag gelöscht → durchgestrichen, nicht mehr im Event buchbar,
            doppelte Zeilen alter Daten) — Snapshot-Name und -Preis, nur verringern (per Zeilen-Id). */}
        {otherArticleLines.map(line => {
          const deleted = line.addOnId == null;
          const duplicate = !deleted && onSiteAddOns.some(a => sameAddOnId(a.addOnId, line.addOnId));
          return (
            <div key={`line-${line.id}`} className="articleBookingRow articleBookingRowDeleted">
              <span className="articleBookingText">
                <span className={`articleBookingName${deleted ? ' articleBookingNameDeleted' : ''}`}>{line.name}</span>
                <span className="articleBookingMeta">{deleted ? 'Katalog-Eintrag gelöscht' : duplicate ? 'weitere Buchung' : 'nicht mehr buchbar'}</span>
              </span>
              <span className="articleBookingPrice">{formatEuro(line.unitPrice)}</span>
              <div className="articleBookingControls">
                <button type="button" className="articleBookingBtn minus" disabled={articleBusy}
                  onClick={() => decrementArticleLine(line.id)} aria-label={`${line.name}: eins weniger`}>−</button>
                <span className="articleBookingQty">{line.quantity}</span>
                <span className="articleBookingBtnSpacer" aria-hidden="true" />
              </div>
            </div>
          );
        })}
        {articleLines.length > 0 && (
          <div className="articleBookingTotal">
            Gesamt Artikel: {formatEuro(linesTotal(articleLines))}
          </div>
        )}
      </Popup>
      <Popup name='confirmpaidprivate' headerText='Als bezahlt markieren' confirmText='Bezahlt'
        onConfirm={handleConfirmPaidPrivate} withCancel type="info"
      >
        <div className="infoPopupBody">
          <p className="infoPopupName">{info.firstName} {info.lastName}</p>
          <div className="infoPopupSection">
            <span className="infoPopupLabel">Betrag</span>
            <span className="infoPopupValue">{formatEuro(info.price)}</span>
          </div>
          {info.email ? (
            <div className="infoPopupSection">
              <CustomTogglebox
                placeholder={'Zahlungsbestätigung senden'}
                checked={sendPaidMail}
                onChange={e => setSendPaidMail(e.target.checked)}
              />
              <p className="payConfirmHint">
                {sendPaidMail
                  ? <>Der Spieler bekommt die Mail „{mailSlotLabel('payment_confirmed')}“ an {info.email} – sofern in der Gruppe ein Textbaustein für die Zahlungsbestätigung hinterlegt ist.</>
                  : 'Es wird keine Mail verschickt – nur der Status wird gesetzt.'}
              </p>
            </div>
          ) : (
            <p className="payConfirmHint">Keine E-Mail-Adresse hinterlegt – es wird keine Bestätigung verschickt.</p>
          )}
        </div>
      </Popup>
      <Popup name='eventplayererror' confirmText='Ok' headerText='Error' type="error">
        <p>{typeof error === 'string' ? error : JSON.stringify(error)}</p>
      </Popup>
    </React.Fragment>
  )
}

export default EventList
